import { Location } from '@angular/common';
import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';

import { CheckPlatformService } from '@services/helper/check-platform.service';
import { ToastService } from '@services/helper/toast.service';
import { UserPermissionService } from '@services/helper/user-permission.service';

/**
 * Permission gate for a route that both creates and edits an entity.
 *
 * FEC-1160 QA (18 Aug): "I gave the master agent permission to view projects
 * only, but he can also edit and archive them." Toggling a capability off on
 * Settings › Permissions did nothing, because the upsert routes were gated on
 * ROLE alone — the capability never reached a guard. `rolePermissionGuard`
 * already understands `data.permissions`, but a single route serves both create
 * (`/project/add`) and edit (`/project/12`), and those need different
 * capabilities, which static route data cannot express.
 *
 * The create/edit split is decided from the `:id` segment: `add` means create.
 *
 * @param createPermission capability required to open the route in create mode
 * @param updatePermission capability required to open it against an existing id
 */
export function entityUpsertPermissionGuard(
  createPermission: string,
  updatePermission: string,
): CanActivateFn {
  return (route) => {
    const permissionService = inject(UserPermissionService);
    const checkPlatformService = inject(CheckPlatformService);
    const location = inject(Location);
    const toast = inject(ToastService);

    // Guards run before the header fetches `/me`, so on a cold load nothing has
    // been loaded yet and every visitor would look unprivileged (FEC-1171).
    if (!checkPlatformService.isBrowser()) {
      return true;
    }

    if (!permissionService.hasLoadedPermissions()) {
      permissionService.initializePermissions();
    }

    const id = route.paramMap.get('id');
    const isCreate = id === 'add' || id === null;
    const required = isCreate ? createPermission : updatePermission;

    if (permissionService.hasPermission(required)) {
      return true;
    }

    toast.error(
      isCreate
        ? 'You do not have permission to create this.'
        : 'You do not have permission to edit this.',
    );
    location.back();

    return false;
  };
}
